import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable, tap } from 'rxjs';
import { User } from '../shared/models/user.model';
import { ApiSearchResponse, PaginatedResponse } from '../interfaces/api';
import { AuthService } from './auth.service';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public errorMsg = '';

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly api: ApiService
  ) {}

  public registerUserWithEmailAddress(newUser: User) {
    const endpoint = `${this.api.url}/auth/register/web/email`;

    return this.http.post(endpoint, newUser);
  }

  public registerSocialUser(newSocialUser: User) {
    const endpoint = `${this.api.url}/auth/register/web/social`;

    return this.http.post(endpoint, newSocialUser);
  }

  public getGoogleUserDetails(token: string): Observable<Object> {
    const endpoint = `https://www.googleapis.com/oauth2/v1/userinfo`;

    const params = new HttpParams()
      .set('access_token', token)
      .set('alt', 'json');

    return this.http.get(endpoint, {
      params,
    });
  }

  public updateUser(
    user: User | Partial<User>,
    runAfterUserUpdate = true
  ): Observable<UpdateUserResponse> {
    const endpoint = `${this.api.url}/users/${user.uuid!}`;
    return this.http.put<UpdateUserResponse>(endpoint, user).pipe(
      tap((response) => {
        // user coverCount becomes undefined when you set the resopnsse to the currentUser
        // so need to get the coverCount from the clone and set the it to the user.response so its not undefined
        response.data.coverCount = user.coverCount;
        this.authService.currentUser = response.data;
        if (runAfterUserUpdate) this.authService.afterUserUpdate();
      })
    );
  }

  public refreshUser(): Observable<any> {
    const uuid = this.authService.currentUser?.uuid;
    const endpoint = `${this.api.url}/users/${uuid!}`;
    return this.http.get<User>(endpoint);
  }

  /**
   * Update the user's cover status and well being status
   * @param isCovered
   *
   * @param hasWellbeing
   */
  public updateConfirmCover(isCovered: boolean, hasWellbeing: boolean): void {
    const clone = User.fromJson(this.authService.currentUser!);

    clone.confirmedCover = isCovered;
    this.updateUser(clone, false).subscribe({
      next: () => {
        if (this.authService.currentUser?.accommodation) {
          this.authService.currentUser.accommodation.covered = isCovered;
          this.authService.currentUser.accommodation.wellbeing = hasWellbeing;
          this.authService.afterUserUpdate();
        }
      },
    });
  }

  /**
   * Service to send a forgot password request
   * @return Observable
   * @param payload
   */
  public forgotPassword(payload: {
    email: string;
    universityCMSID: string | null;
  }): Observable<any> {
    const endpoint = `${this.api.url}/auth/forgot-password`;
    return this.http.post(endpoint, payload);
  }

  /**
   * A service to send a vertification email
   * @param emailAddress string
   * @returns Observable
   */
  public resendVerificationEmail(emailAddress: string): Observable<any> {
    const endpoint = `${this.api.url}/users/${emailAddress}/resend-verify-email`;
    return this.http.post(endpoint, {});
  }

  /**
   * @param emailAddress string
   * @returns Observable
   */
  public resendUnauthenticatedVerificationEmail(
    emailAddress: string
  ): Observable<any> {
    const endpoint = `${this.api.url}/unauthenticated/${emailAddress}/resend-verify-email`;
    return this.http.post(endpoint, {});
  }

  public refer(userUuid: string, emails: string[]): Observable<void> {
    const endpoint = `${this.api.url}/users/${userUuid}/refer-a-friend`;
    return this.http.post<void>(endpoint, { emails });
  }

  public addUserIDToURL(url: string): string {
    if (this.authService.currentUser?.uuid && url.match(/{userid}/gi)) {
      return url.replace(/{userid}/gi, this.authService.currentUser.uuid);
    }
    // if user not logged in or the url doesn't contain {userid}, just return the url as is
    return url;
  }

  /**
   * A service to search for users by a given string
   * @param search string
   * @returns
   */
  public search(search: string, page: number = 1): Observable<PaginatedResponse<User>> {
    const params = new HttpParams()
      .set('search', search)
      .set('page', page.toString());

    return this.http.get<PaginatedResponse<User>>(
      `${this.api.url}/admin/users/search`,
      { params }
    );
  }

  /**
   * A service to add a new user role when given the user object and string based role
   * @param user User
   * @param role string
   * @returns
   */
  public addRole(user: User, role: string): Observable<any> {
    return this.http.put(
      `${this.api.url}/admin/users/${user.uuid!}/roles/${role}/add`,
      {}
    );
  }

  /**
   * A service to remove a user role when given a User object and the string based role
   * @param user User
   * @param role string
   * @returns Observable
   */
  public removeRole(user: User, role: string): Observable<any> {
    return this.http.delete(
      `${this.api.url}/admin/users/${user.uuid!}/roles/${role}/remove`,
      {}
    );
  }

  /**
   * A service to soft delete a user fom the database
   * @param user User
   * @returns Observable
   */
  public deleteUser(user: User): Observable<any> {
    return this.http.delete(`${this.api.url}/users/${user.uuid!}`);
  }

  /**
   * An admin service to hard delete a user from the database
   * @param user User
   * @returns Observable
   */
  public hardDeleteUser(user: User): Observable<any> {
    return this.http.delete(`${this.api.url}/admin/users/${user.uuid!}`);
  }

  public getPriceBasedOnPostcode(user: User): Observable<any> {
    return this.http.get(
      `${this.api.url}/users/${user.uuid ?? ''}/calc-price-on-postcode`
    );
  }

  public acceptMarketingConsentByUUID(uuid: string) {
    return this.http.get(
      `${this.api.url}/unauthenticated/marketing-consent/${uuid}`
    );
  }
}

export interface UpdateUserResponse extends ApiSearchResponse {
  data: User;
}
